import { useState, useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { Calendar } from "lucide-react";
import { useMe } from "../context/MeContext.js";

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  min?: string | undefined;
  max?: string | undefined;
  style?: CSSProperties;
  isUS?: boolean;
}

function toDisplay(iso: string, isUS: boolean): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return isUS ? `${m}/${d}/${y}` : `${d}/${m}/${y}`;
}

function toISO(display: string, isUS: boolean): string {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(display)) return "";
  const [p1, p2, p3] = display.split("/");
  return isUS ? `${p3}-${p1}-${p2}` : `${p3}-${p2}-${p1}`;
}

function formatDigits(digits: string): string {
  const d = digits.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

export interface DateFieldOptions {
  value: string;
  onChange: (v: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  isUS?: boolean | undefined;
}

// The typed-date behaviour behind every date field: digit masking into
// DD/MM/YYYY (or MM/DD/YYYY for US users), range and calendar validation, and
// the hidden native <input type="date"> that supplies the calendar popup.
// DatePicker and ui/date-input both use it, so the two can only ever differ in
// how they look.
export function useDateField({ value, onChange, min, max, isUS: isUSProp }: DateFieldOptions) {
  const { isUS: ctxUS } = useMe();
  const isUS = isUSProp ?? ctxUS;
  const hiddenRef = useRef<HTMLInputElement>(null);

  const [display, setDisplay] = useState(() => toDisplay(value, isUS));
  const [error, setError] = useState(false);

  useEffect(() => {
    if (toISO(display, isUS) !== value) {
      setDisplay(toDisplay(value, isUS));
      setError(false);
    }
  }, [value, isUS]);

  function handleChange(raw: string) {
    const digits = raw.replace(/\D/g, "");
    const formatted = formatDigits(digits);
    setDisplay(formatted);

    if (formatted === "") {
      setError(false);
      onChange("");
      return;
    }

    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(formatted)) {
      setError(true);
      onChange("");
      return;
    }

    const iso = toISO(formatted, isUS);
    const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
    const dt = new Date(y, m - 1, d);
    const valid =
      dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d &&
      y >= 1900 && y <= 2100 &&
      (!min || iso >= min) &&
      (!max || iso <= max);

    setError(!valid);
    if (valid) onChange(iso);
    else onChange("");
  }

  function handlePickerChange(iso: string) {
    if (!iso) return;
    setDisplay(toDisplay(iso, isUS));
    setError(false);
    onChange(iso);
  }

  function openPicker() {
    hiddenRef.current?.showPicker?.();
  }

  return {
    display,
    error,
    hiddenRef,
    handleChange,
    handlePickerChange,
    openPicker,
    placeholder: isUS ? "MM/DD/YYYY" : "DD/MM/YYYY",
  };
}

export function DatePicker({ value, onChange, disabled, min, max, style, isUS }: Props) {
  const { display, error, hiddenRef, handleChange, handlePickerChange, openPicker, placeholder } =
    useDateField({ value, onChange, min, max, isUS });

  const inp: CSSProperties = {
    padding: "8px 10px",
    border: `1px solid ${error ? "var(--color-error)" : "var(--color-surface-200)"}`,
    borderRadius: "var(--radius-md)", fontSize: "14px",
    background: disabled ? "var(--color-surface-50)" : "var(--color-surface-0)",
    color: disabled ? "var(--color-surface-600)" : "inherit",
    cursor: disabled ? "not-allowed" : "text",
    outline: "none", flex: 1, minWidth: "90px", boxSizing: "border-box" as const,
    ...style,
  };

  const iconBtn: CSSProperties = {
    padding: "8px 10px", flexShrink: 0,
    border: `1px solid ${error ? "var(--color-error)" : "var(--color-surface-200)"}`,
    borderLeft: "none",
    borderRadius: "0 var(--radius-md) var(--radius-md) 0",
    background: disabled ? "var(--color-surface-50)" : "var(--color-surface-0)",
    cursor: disabled ? "not-allowed" : "pointer",
    color: "var(--color-surface-500)",
    lineHeight: 1, fontSize: 14,
    display: "flex", alignItems: "center", justifyContent: "center",
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, position: "relative", width: "100%" }}>
      <input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={display}
        disabled={disabled}
        onChange={e => handleChange(e.target.value)}
        style={{ ...inp, borderRadius: "var(--radius-md) 0 0 var(--radius-md)" }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={openPicker}
        tabIndex={-1}
        aria-label="Open calendar"
        style={iconBtn}
      >
        <Calendar size={16} />
      </button>
      <input
        ref={hiddenRef}
        type="date"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={e => handlePickerChange(e.target.value)}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0, top: 0, left: 0 }}
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  );
}
